"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

// ---------------------------------------------------------------------------
// Obsługa dotyku (telefony / tablety)
//
// Radix Tooltip z założenia otwiera się tylko po hover/focus — na ekranach
// dotykowych nigdy się nie pokazuje, bo nie ma zdarzenia hover. Owijam więc
// `Root` własnym wrapperem, który:
//   • trzyma stan `open` (kontrolowany), respektując `open`/`onOpenChange`
//     przekazane z zewnątrz,
//   • udostępnia setter przez kontekst, dzięki czemu `TooltipTrigger` może
//     wymusić otwarcie po tapnięciu (`onPointerDown` z `pointerType==="touch"`).
//
// Zamknięcie nadal obsługuje Radix automatycznie (tap poza, Esc, scroll).
// ---------------------------------------------------------------------------

const TooltipOpenCtx = React.createContext<((v: boolean) => void) | null>(null);

type TooltipRootProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>;

const Tooltip: React.FC<TooltipRootProps> = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  children,
  ...rootProps
}) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const isControlled = openProp !== undefined;
  const value = isControlled ? openProp : internalOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <TooltipOpenCtx.Provider value={handleOpenChange}>
      <TooltipPrimitive.Root open={value} onOpenChange={handleOpenChange} {...rootProps}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipOpenCtx.Provider>
  );
};

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onPointerDown, onClick, ...props }, ref) => {
  const setOpen = React.useContext(TooltipOpenCtx);

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onPointerDown={(e) => {
        // Tylko dotyk — myszka i pióro mają hover, nie chcemy podwójnej
        // logiki ani otwierania tooltipa kliknięciem na desktopie (bo to
        // koliduje z elementami klikalnymi typu link).
        if (e.pointerType === "touch") {
          setOpen?.(true);
        }
        onPointerDown?.(e);
      }}
      onClick={(e) => {
        // Fallback dla środowisk, które nie raportują pointerType (rzadko,
        // ale np. niektóre WebView). Sprawdzamy „coarse pointer" — jeśli
        // główny wskaźnik to dotyk, otwieramy.
        if (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches) {
          setOpen?.(true);
        }
        onClick?.(e);
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        "z-50 max-w-[calc(100vw-1rem)] overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin)",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
